/**
 * File: category_articles_list.tsx
 * Description: Shared shell that renders a grouped, scrollable list of FAQ articles
 * organised by category. Callers control what each article's ListItem renders via the
 * itemContent and secondaryAction render props.
 */

import { Fragment, useMemo, type ReactNode } from 'react';
import {
  List,
  ListItem,
  ListSubheader,
  Typography,
  useTheme
} from '@mui/material';
import type { Category, Article, ExtendedArticle } from '@/interfaces/category';
import { flattenCategoryArticles } from '@/utils/flatten_articles';
import { ColorsPalletTheme } from '@/constants/colors_pallet_theme';

/**
 * Props for the CategoryArticlesList component.
 *
 * @property {Category[]} categories - Categories with their nested articles to display, grouped by category.
 * @property {(category: Category, article: Article) => ReactNode} itemContent - Renders the main content of an article's
 *    ListItem (e.g. plain text, or a link/button wrapping it).
 * @property {(category: Category, article: Article) => ReactNode} [secondaryAction] - Optional renderer for a
 *    trailing action shown at the end of an article's ListItem (e.g. an icon button).
 */
interface CategoryArticlesListProps {
  categories: Category[];
  itemContent: (category: Category, article: Article) => ReactNode;
  secondaryAction?: (category: Category, article: Article) => ReactNode;
}

/**
 * Renders a grouped, scrollable list of FAQ articles organised by category. Owns the
 * List/ListSubheader/ListItem structure, styling and the empty-state message; callers
 * decide what each article renders via itemContent and, optionally, secondaryAction.
 *
 * @param {Category[]} props.categories - Categories with their nested articles to display.
 * @param {(category: Category, article: Article) => ReactNode} props.itemContent - Renders each article's main content.
 * @param {(category: Category, article: Article) => ReactNode} [props.secondaryAction] - Renders each article's trailing action, if any.
 * @returns {JSX.Element} The rendered article list, or a fallback message when no articles are present.
 */
function CategoryArticlesList({ categories, itemContent, secondaryAction }: CategoryArticlesListProps) {

  // Main App Theme
  const theme = useTheme();
  const colors = ColorsPalletTheme(theme.palette.mode);

  // Flatten all categories into a single array to check whether any articles exist at all.
  const allArticlesExtended = useMemo<ExtendedArticle[]>(() => flattenCategoryArticles(categories), [categories]);

  return allArticlesExtended.length === 0 ? (
    <Typography color="text.secondary">
      No se encontraron artículos para este filtro
    </Typography>
  ) : (
    <List 
      dense 
      sx={{
        border: 2,
        borderColor: colors.gray[800],
        paddingTop: 0,
        paddingBottom: 0,
        maxHeight: { xs: '51vh', sm: '55vh' },
        overflowY: 'auto'
      }}
      data-testid='category-articles-list'
    >
      {categories.map(
        category =>
          category.articles.length > 0 && (
            <Fragment key={category.id}>
              <ListSubheader sx={{ background: colors.gray[900] }}>
                <Typography variant="h6">{category.title}</Typography>
              </ListSubheader>
              {category.articles.toReversed().map(article => (
                  <ListItem
                    key={article.id}
                    sx={{ background: colors.gray[950] }}
                    secondaryAction={secondaryAction?.(category, article)}
                  >
                    {itemContent(category, article)}
                  </ListItem>
                ))
              }
            </Fragment>
          )
      )}
    </List>
  );
}

export default CategoryArticlesList;
